import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';

import { AuditClient } from '@@api';
import { useApiClient } from '@@common';
import useDebouncedCallback from '../../common/hooks/useDebouncedCallback';

type Props = {
	field: 'source' | 'type' | 'action' | 'attributeKey';
	label: string;
	value: string;
	onChange: (value: string) => void;
	size?: 'small' | 'medium';
};

export default function AuditFilterAutocomplete({ field, label, value, onChange, size }: Props) {
	const client = useApiClient(AuditClient);
	const [open, setOpen] = useState(false);
	const [prefix, setPrefix] = useState('');
	const updatePrefix = useDebouncedCallback(setPrefix, 300);
	const lookup = useQuery({
		queryKey: ['audit', 'filter-values', field, prefix],
		queryFn: ({ signal }) => client.getFilterValues(field, prefix || undefined, signal),
		enabled: open,
	});
	const values = lookup.data ?? [];
	const options = value && !values.includes(value) ? [value, ...values] : values;

	return (
		<Autocomplete
			fullWidth
			size={size}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => { setOpen(false); updatePrefix(''); }}
			options={options}
			value={value || null}
			onChange={(_, selected) => onChange(selected ?? '')}
			onInputChange={(_, input, reason) => {
				if (reason === 'input' || reason === 'clear') {
					updatePrefix(input);
				}
			}}
			filterOptions={(items) => items}
			loading={lookup.isFetching}
			noOptionsText={lookup.isError ? 'Options could not be loaded.' : 'No matching values'}
			renderInput={(params) => <TextField {...params} label={label} />}
		/>
	);
}
